// Fire a throttled callback when the tab regains focus or becomes visible.
//
// Long-lived SSE streams can silently die while a tab is backgrounded or the
// machine sleeps (the server closes idle connections; auto-reconnect gives up
// after a few attempts). When the user comes back, nothing re-syncs and the
// UI shows stale state until a full page reload. This composable is the
// recovery hook: subscribe a "refresh" callback that runs on return-to-tab.
//
// Same pattern LocalStatusMenu uses for the local bridge — extracted here
// so the notifications streams share one implementation.
//
// Mounts add the listeners; unmount removes them. Does NOT fire on mount —
// the consumer's initial fetch/stream-open already covers first paint.

import { onMounted, onUnmounted } from 'vue'

export interface VisibilityRefreshOptions {
  // Minimum gap between callback runs. A rapid focus/visibility flurry (e.g.
  // alt-tabbing) collapses to one run.
  throttleMs?: number
}

const DEFAULT_THROTTLE_MS = 4_000

export function useVisibilityRefresh(
  callback: () => void,
  options: VisibilityRefreshOptions = {},
): void {
  const throttleMs = options.throttleMs ?? DEFAULT_THROTTLE_MS
  // Negative infinity so the first focus always runs, regardless of clock.
  let lastRun = Number.NEGATIVE_INFINITY

  function run(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    const now = Date.now()
    if (now - lastRun < throttleMs) return
    lastRun = now
    callback()
  }

  function onVisibility(): void {
    // visibilitychange fires on both hide and show — only act on show.
    if (typeof document === 'undefined' || document.visibilityState === 'visible') run()
  }

  onMounted(() => {
    window.addEventListener('focus', run)
    document.addEventListener('visibilitychange', onVisibility)
  })

  onUnmounted(() => {
    window.removeEventListener('focus', run)
    document.removeEventListener('visibilitychange', onVisibility)
  })
}
